<template>
  <!-- Cover Image Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="true"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Cover Image</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="coverImageUrl" class="image-preview-container">
          <img :src="coverImageUrl" alt="Cover Preview" class="image-preview" />
          <button class="remove-image-button" @click="removeCoverImage">
            <v-icon>mdi-delete</v-icon>
            Remove Image
          </button>
        </div>
        <div v-else class="empty-image-message">
          No cover image selected. Please upload an image for your lesson plan.
        </div>
        <label class="upload-button">
          {{ coverImageUrl ? "Change Image" : "Upload Image" }}
          <input
            ref="fileInputRef"
            type="file"
            @change="handleCoverImageUpload"
            accept="image/*"
            style="display: none"
          />
        </label>
      </div>
    </template>
  </AccordionItem>

  <!-- Loading State -->
  <div v-if="isLoading" class="loading-container">
    <p>Loading categories...</p>
  </div>

  <!-- Empty State -->
  <div v-else-if="tagSections.length === 0" class="empty-state">
    <p>No categories available.</p>
  </div>

  <!-- Dynamic Tag Sections -->
  <template v-else>
    <!-- Other Categories Section -->
    <template v-if="hasNonStandardsCategories">
      <!-- Non-Standards Categories -->
      <AccordionItem
        v-for="section in nonStandardsCategories"
        :key="section.id"
        class="right-sidebar-item"
        :icon-position="`left`"
        :collapsedIcon="`/images/arrow-bottom.svg`"
        :expandedIcon="`/images/arrow-bottom.svg`"
        @toggle="(isOpen) => handleAccordionToggle(isOpen, section.id)"
      >
        <template v-slot:title>
          <div class="right-sidebar-title">{{ section.title }}</div>
        </template>
        <template v-slot:content>
          <div class="content-container">
            <!-- Selected tags display -->
            <div class="tags-container" v-if="section.selectedItems.length > 0">
              <div
                v-for="(tag, index) in section.selectedItems"
                :key="index"
                class="tag"
              >
                <p>{{ getTagDisplayText(tag, section) }}</p>
                <button
                  @click="removeTag(section, index)"
                  class="delete-tag-button"
                >
                  <v-icon>mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div v-else class="empty-tags-message">No tags selected</div>

            <div class="select-container">
              <MultipleSelect
                v-model="section.selectedValues"
                :placeholder="`Select ${section.title}...`"
                :options="section.formattedOptions"
                maxHeight="200px"
                variant="form"
                :teleport="true"
                :match-trigger-width="true"
                @update:modelValue="handleMultipleSelectionChange(section)"
              />
            </div>

            <div
              v-if="section.isLoadingOptions"
              class="options-loading-indicator"
            >
              Loading options...
            </div>
          </div>
        </template>
      </AccordionItem>
    </template>

    <!-- Standards Section -->
    <AccordionItem
      v-if="hasStandardsCategories"
      class="right-sidebar-item standards-main-accordion"
      :icon-position="`left`"
      :collapsedIcon="`/images/arrow-bottom.svg`"
      :expandedIcon="`/images/arrow-bottom.svg`"
      :initiallyOpen="false"
    >
      <template v-slot:title>
        <div class="right-sidebar-title standards-main-title">Standards</div>
      </template>
      <template v-slot:content>
        <div class="standards-accordion-content">
          <!-- Standards Categories -->
          <AccordionItem
            v-for="section in standardsCategories"
            :key="section.id"
            class="right-sidebar-item standards-category"
            :icon-position="`left`"
            :collapsedIcon="`/images/arrow-bottom.svg`"
            :expandedIcon="`/images/arrow-bottom.svg`"
            @toggle="(isOpen) => handleAccordionToggle(isOpen, section.id)"
          >
            <template v-slot:title>
              <div class="right-sidebar-title">{{ section.title }}</div>
            </template>
            <template v-slot:content>
              <div class="content-container">
                <!-- Selected tags display -->
                <div
                  class="tags-container"
                  v-if="section.selectedItems.length > 0"
                >
                  <div
                    v-for="(tag, index) in section.selectedItems"
                    :key="index"
                    class="tag"
                  >
                    <p>{{ getTagDisplayText(tag, section) }}</p>
                    <button
                      @click="removeTag(section, index)"
                      class="delete-tag-button"
                    >
                      <v-icon>mdi-close</v-icon>
                    </button>
                  </div>
                </div>
                <div v-else class="empty-tags-message">No tags selected</div>

                <div class="select-container">
                  <MultipleSelect
                    v-model="section.selectedValues"
                    :placeholder="`Select ${section.title}...`"
                    :options="section.formattedOptions"
                    maxHeight="200px"
                    variant="form"
                    :teleport="true"
                    :match-trigger-width="true"
                    :show-search="true"
                    order="a-z"
                    :input-placeholder="`Search ${section.title}...`"
                    @update:modelValue="handleMultipleSelectionChange(section)"
                  />
                </div>

                <div
                  v-if="section.isLoadingOptions"
                  class="options-loading-indicator"
                >
                  Loading options...
                </div>
              </div>
            </template>
          </AccordionItem>
        </div>
      </template>
    </AccordionItem>
  </template>

  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Permalink</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div class="permalink-container">
          <BaseInput
            type="text"
            class="permalink-input"
            v-model="permalink"
            @input="updatePermalink"
            placeholder="Enter permalink"
          />
        </div>
      </div>
    </template>
  </AccordionItem>
</template>

<script setup>
import BaseInput from "@/components/BaseInput.vue";
import MultipleSelect from "@/components/common/MultipleSelect.vue";
import categoryAdminService from "@/services/categoryAdminService";
import { CATEGORY_TYPES } from "@/utils/constants";
import AccordionItem from "@/views/ChinaUs/AccordionItem.vue";
import {
  defineEmits,
  defineExpose,
  onMounted,
  reactive,
  ref,
  watch,
  computed,
} from "vue";

const emit = defineEmits([
  "cover-image-updated",
  "tag-sections-updated",
  "permalink-updated",
]);

const coverImageUrl = ref(null);
const fileInputRef = ref(null); // Reference to the file input element
const isLoading = ref(true);
const permalink = ref(""); // Add permalink reference
const loadedOptions = ref(new Set()); // Track which categories have loaded options
const standardsLoaded = ref(false); // Track if standards are loaded
const standardsLoading = ref(false); // Track if standards are currently loading

// Standards data structure
const standardsSection = reactive({
  id: "standards", // Use a special ID for the standards section
  title: "Standards",
  options: [], // Will hold grouped standards
  formattedOptions: [], // Options formatted for BaseInput
  selectedItems: [], // Text representation of selected items
  selectedValues: [], // Values for v-model binding
  isLoadingOptions: false,
  groupedOptions: {}, // Will contain all standards grouped by category
});

// Define the tag sections with initial empty state
const tagSections = reactive([]);

// Computed properties for organizing categories
const standardsCategories = computed(() => {
  return tagSections.filter((section) => isStandardCategory(section));
});

const nonStandardsCategories = computed(() => {
  return tagSections.filter((section) => !isStandardCategory(section));
});

const hasStandardsCategories = computed(() => {
  return standardsCategories.value.length > 0;
});

const hasNonStandardsCategories = computed(() => {
  return nonStandardsCategories.value.length > 0;
});

// Helper method to identify if a category is a standard
const isStandardCategory = (section) => {
  if (!section || !section.name) return false;
  return section.name.toLowerCase().includes("standard");
};

const handleCoverImageUpload = (event) => {
  const file = event.target.files[0];
  if (file && file.type.startsWith("image/")) {
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image file size should be less than 5MB.");
      event.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      coverImageUrl.value = e.target.result;
      emit("cover-image-updated", coverImageUrl.value);
    };
    reader.readAsDataURL(file);
  } else if (file) {
    // Only show error if a file was actually selected
    // Handle invalid file type
    alert("Please select an image file.");
    // Reset file input
    event.target.value = null;
  }
};

// Add function to remove the cover image
const removeCoverImage = () => {
  coverImageUrl.value = null;
  emit("cover-image-updated", null);

  // Reset the file input element to ensure it can trigger onChange for the same file
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

// Fetch categories from API
const fetchCategories = async () => {
  try {
    isLoading.value = true;
    const response = await categoryAdminService.getCategoriesLesson();

    if (response && response.data.data && response.data.data.rows) {
      // Clear existing tag sections
      tagSections.splice(0, tagSections.length);

      // Create tag sections from categories
      for (const category of response.data.data.rows) {
        const isLessonPlanCategory =
          category.category_for?.some(
            (item) => item === CATEGORY_TYPES.LessonPlan
          ) || false;

        if (!isLessonPlanCategory) continue;

        const section = {
          id: category.id,
          name: category.name, // Add name property for standard identification
          title: category.title,
          options: [],
          formattedOptions: [], // Options formatted for BaseInput
          selectedItems: [], // Text representation of selected items
          selectedValues: [], // Values for v-model binding
          isLoadingOptions: false,
        };

        tagSections.push(section);
      }
    }
  } catch (error) {
    console.error("Error fetching categories:", error);
  } finally {
    isLoading.value = false;
  }
};

// Format options for BaseInput component
const formatOptions = (options, isStandard = false) => {
  if (isStandard) {
    const formattedOptions = [];

    // Group headers as disabled options
    for (const [category, terms] of Object.entries(
      standardsSection.groupedOptions
    )) {
      // Add group header
      formattedOptions.push({
        text: `--- ${category} ---`,
        value: `group_${category}`,
        disabled: true,
      });

      // Add standard items
      for (const term of terms) {
        formattedOptions.push({
          text: term.title,
          value: JSON.stringify({
            id: term.id,
            title: term.title,
            standardCategory: term.standardCategory,
          }),
        });
      }
    }

    return formattedOptions;
  } else {
    // Regular options formatting
    return options.map((option) => ({
      text: option.title || option,
      value: JSON.stringify({
        id: option.id || option,
        title: option.title || option,
      }),
    }));
  }
};

// Get display text for a tag based on its value
const getTagDisplayText = (tagValue, section) => {
  try {
    const parsedValue = JSON.parse(tagValue);
    if (section.id === "standards" && parsedValue.standardCategory) {
      return `${parsedValue.standardCategory}: ${parsedValue.title}`;
    }
    return parsedValue.title;
  } catch (e) {
    // Fallback for any unparseable values
    return tagValue;
  }
};

// Fetch options for a specific category
const fetchCategoryOptions = async (categoryId) => {
  // Skip if options are already loaded or currently loading
  const sectionIndex = tagSections.findIndex(
    (section) => section.id === categoryId
  );
  if (
    sectionIndex === -1 ||
    loadedOptions.value.has(categoryId) ||
    tagSections[sectionIndex].isLoadingOptions
  ) {
    return;
  }

  try {
    // Set loading state for this specific category
    tagSections[sectionIndex].isLoadingOptions = true;

    const response = await categoryAdminService.getById(categoryId);

    if (response && response.data.data && response.data.data.terms) {
      const filteredTerms = response.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      // Update options with term objects containing id and title
      const terms = filteredTerms.map((term) => ({
        id: term.id,
        title: term.title,
      }));
      tagSections[sectionIndex].options = terms;

      // Format options for BaseInput
      tagSections[sectionIndex].formattedOptions = formatOptions(terms);

      // Mark this category as having loaded options
      loadedOptions.value.add(categoryId);
    }
  } catch (error) {
    console.error(`Error fetching options for category ${categoryId}:`, error);
  } finally {
    if (sectionIndex !== -1) {
      tagSections[sectionIndex].isLoadingOptions = false;
    }
  }
};

// Fetch standards categories
const fetchStandardsCategories = async () => {
  if (standardsLoaded.value || standardsLoading.value) {
    return;
  }

  try {
    standardsSection.isLoadingOptions = true;
    standardsLoading.value = true;

    const response = await categoryAdminService.getStandardCategories();
    console.log("Standards response:", response);

    if (
      response &&
      response.data &&
      response.data.data &&
      response.data.data.rows
    ) {
      const standardCategories = response.data.data.rows;

      // Clear existing grouped options
      standardsSection.groupedOptions = {};

      // Fetch options for each standard category
      for (const standardCategory of standardCategories) {
        await fetchStandardCategoryOptions(standardCategory);
      }

      // Format options for BaseInput
      standardsSection.formattedOptions = formatOptions([], true);

      // Mark standards as loaded
      standardsLoaded.value = true;
    }
  } catch (error) {
    console.error("Error fetching standard categories:", error);
  } finally {
    standardsSection.isLoadingOptions = false;
    standardsLoading.value = false;
  }
};

// Fetch options for a specific standard category
const fetchStandardCategoryOptions = async (standardCategory) => {
  try {
    const response = await categoryAdminService.getById(standardCategory.id);

    if (response && response.data.data && response.data.data.terms) {
      // Store terms for this standard category
      standardsSection.groupedOptions[standardCategory.title] =
        response.data.data.terms.map((term) => ({
          id: term.id,
          title: term.title,
          standardCategory: standardCategory.title,
        }));

      // Update formatted options for BaseInput
      standardsSection.formattedOptions = formatOptions([], true);
    }
  } catch (error) {
    console.error(
      `Error fetching options for standard category ${standardCategory.id}:`,
      error
    );
  }
};

// Handle accordion toggle to load options when opened
const handleAccordionToggle = (isOpen, categoryId) => {
  console.log("handleAccordionToggle", isOpen, categoryId);
  if (isOpen) {
    if (categoryId === "standards") {
      fetchStandardsCategories();
    } else if (!loadedOptions.value.has(categoryId)) {
      fetchCategoryOptions(categoryId);
    }
  }
};

// Update permalink function
const updatePermalink = (event) => {
  permalink.value = event.target.value;
  emit("permalink-updated", permalink.value);
};

// Method to update from parent data
const updateFromParent = (newCoverImage, newTagSections, newPermalink) => {
  console.log("RightSidebar: Updating from parent data", {
    newCoverImage,
    newTagSections,
    newPermalink,
  });

  // Handle cover image
  if (newCoverImage) {
    coverImageUrl.value = newCoverImage;
  } else {
    coverImageUrl.value = null;
    // Make sure file input is reset
    if (fileInputRef.value) {
      fileInputRef.value.value = "";
    }
  }

  // Update permalink if provided
  if (newPermalink !== undefined) {
    permalink.value = newPermalink;
  }

  // Only process tag sections when they exist
  if (newTagSections && newTagSections.length > 0) {
    // Ensure all categories are loaded first
    if (tagSections.length === 0 || isLoading.value) {
      // If categories aren't loaded yet, store the data and process after loading
      const tempTagSections = [...newTagSections];
      // Use a watcher to apply the sections once loading is complete
      const unwatch = watch(isLoading, (newValue) => {
        if (!newValue && tagSections.length > 0) {
          // Now that categories are loaded, process the sections
          processTagSections(tempTagSections);
          // Remove the watcher
          unwatch();
        }
      });
    } else {
      // Categories are already loaded, process directly
      processTagSections(newTagSections);
    }
  }
};

// Helper function to process tag sections
const processTagSections = (newTagSections) => {
  // Map the incoming sections to the existing tagSections
  newTagSections.forEach((newSection) => {
    // Find the matching section in tagSections
    const existingSection = tagSections.find(
      (section) =>
        section.id === newSection.id || section.title === newSection.title
    );

    if (existingSection) {
      // Ensure the section options are loaded if needed
      if (
        !loadedOptions.value.has(existingSection.id) &&
        existingSection.id !== "standards"
      ) {
        fetchCategoryOptions(existingSection.id).then(() => {
          // After options are loaded, update the selections
          updateSectionSelections(existingSection, newSection.selected);
        });
      } else if (existingSection.id === "standards" && !standardsLoaded.value) {
        // Handle standards section separately
        fetchStandardsCategories().then(() => {
          updateSectionSelections(existingSection, newSection.selected);
        });
      } else {
        // Options already loaded, update selections immediately
        updateSectionSelections(existingSection, newSection.selected);
      }
    }
  });
};

// Helper function to update section selections
const updateSectionSelections = (section, selectedItems) => {
  if (!selectedItems || !selectedItems.length) return;

  // Process each selected item to ensure it's in the correct format
  const formattedItems = selectedItems.map((item) => {
    // If the item is already a string that looks like JSON, keep it as is
    if (typeof item === "string" && item.startsWith("{")) {
      return item;
    }

    // Otherwise, try to convert it to our expected format
    try {
      // If it's already a parsed object with id and title
      if (typeof item === "object" && item.id) {
        return JSON.stringify(item);
      }

      // If it's a string but not JSON formatted, create a new object
      return JSON.stringify({
        id: item,
        title: item,
      });
    } catch (e) {
      console.error("Error formatting selected item:", e);
      return item; // fallback to original value
    }
  });

  // Update the section with the formatted selections
  section.selectedItems = formattedItems;
  section.selectedValues = [...formattedItems];

  // Force UI update
  setTimeout(() => {
    section.selectedValues = [...formattedItems];
  }, 0);
};

// Expose the method to parent
defineExpose({
  updateFromParent,
});

// Handle multiple selection change
const handleMultipleSelectionChange = (section) => {
  // Update selectedItems to match selectedValues
  section.selectedItems = [...section.selectedValues];
  emitUpdatedTagSections();
};

// Remove a tag from selected items
const removeTag = (section, index) => {
  // Remove from selectedItems
  section.selectedItems.splice(index, 1);

  // Reset selectedValues completely to ensure sync
  section.selectedValues = [...section.selectedItems];

  // Force BaseInput to update by creating a new array
  // This ensures the checkboxes reflect the current state
  setTimeout(() => {
    // Small delay to ensure DOM updates
    section.selectedValues = [...section.selectedItems];
  }, 0);

  emitUpdatedTagSections();
};

// Emit updated tag sections
const emitUpdatedTagSections = () => {
  // Format the tag sections for emit
  const formattedTagSections = tagSections.map((section) => ({
    id: section.id,
    title: section.title,
    selected: section.selectedItems || [],
  }));

  emit("tag-sections-updated", formattedTagSections);
};

// Watch for changes in tagSections and emit updates
watch(
  tagSections,
  () => {
    emitUpdatedTagSections();
  },
  { deep: true }
);

// Emit initial cover image if available
if (coverImageUrl.value) {
  emit("cover-image-updated", coverImageUrl.value);
}

// Fetch categories when component is mounted
onMounted(() => {
  fetchCategories();
});
</script>

<style>
/* Unscoped: teleported dropdowns render outside this component's scope */
.select-dropdown.variant-form {
  height: auto;
  width: auto;
  min-width: 360px;
}
</style>

<style scoped>

.right-sidebar-title {
  width: 100%;
  margin-left: 5px;
  font-weight: 500;
  font-size: 1em;
  color: var(--primary-blue);
  letter-spacing: 0.01em;
}

.right-sidebar-item {
  border-bottom: 1px solid #d8dae6;
  padding-top: 1rem;
  padding-bottom: 1rem;
  transition: background-color 0.2s ease;
}

/* Loading and Empty States */
.loading-container,
.empty-state {
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 0.9em;
  border-bottom: 1px solid #d8dae6;
}

.loading-container p,
.empty-state p {
  margin: 0;
}

.empty-tags-message {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  padding: 8px 0;
}

.options-loading-indicator {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  margin-top: 5px;
  text-align: center;
  padding: 5px;
  background-color: rgba(0, 61, 106, 0.03);
  border-radius: 4px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Cover Image Styles */
.image-preview-container {
  margin-bottom: 12px;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  background-color: #f8f9fa;
  position: relative;
  display: flex;
  flex-direction: column;
}

.empty-image-message {
  padding: 15px;
  background-color: rgba(0, 61, 106, 0.03);
  border: 1px dashed rgba(0, 61, 106, 0.2);
  border-radius: 6px;
  color: #666;
  font-size: 0.9em;
  text-align: center;
  margin-bottom: 12px;
}

.remove-image-button {
  margin-top: 8px;
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  align-self: center;
  margin-bottom: 8px;
}

.remove-image-button:hover {
  background-color: rgba(220, 53, 69, 0.2);
}

.image-preview {
  max-width: 100%;
  max-height: 160px;
  border-radius: 6px;
  display: block;
  object-fit: cover;
  width: 100%;
}

.upload-button {
  display: inline-block;
  padding: 10px 18px;
  background-color: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  font-size: 0.9em;
  font-weight: 500;
  transition: all 0.2s ease;
  width: fit-content;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.upload-button:hover {
  background-color: #00497e;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.upload-button:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Select Container Styles */
.select-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (min-width: 768px) {
  .select-container {
    flex-direction: row;
    align-items: center;
  }

  .select-container .input-wrap {
    flex-grow: 1;
  }

  .select-container .add-button {
    min-width: 60px;
    width: auto;
    margin-top: 0;
  }
}

/* Tag Styles */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 0; /* Remove minimum height to fix spacing */
}

.tags-container:not(:empty) {
  margin-bottom: 12px; /* Add margin only if there are tags */
  background-color: rgba(0, 61, 106, 0.03);
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 61, 106, 0.07);
}

.tag {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.07) 0%,
    rgba(0, 61, 106, 0.12) 100%
  );
  color: var(--primary-blue);
  padding: 6px 12px;
  border-radius: 18px;
  font-size: 0.85em;
  font-weight: 500;
  border: 1px solid rgba(0, 61, 106, 0.1);
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.tag:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.1) 0%,
    rgba(0, 61, 106, 0.15) 100%
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.delete-tag-button {
  background: rgba(255, 255, 255, 0.6);
  border: none;
  color: var(--primary-blue);
  margin-left: 6px;
  cursor: pointer;
  font-size: 0.6em;
  line-height: 1;
  padding: 1px;
  opacity: 0.8;
  transition: all 0.2s ease;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-tag-button:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.9);
  color: #e63946;
}

.add-button {
  padding: 10px 16px;
  background: linear-gradient(135deg, #00925e 0%, #007c50 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.add-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a06a 0%, #008a59 100%);
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.add-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.add-button:disabled {
  background: linear-gradient(135deg, #e2e2e2 0%, #d8d8d8 100%);
  color: #999;
  cursor: not-allowed;
  box-shadow: none;
}

/* Add subtle animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.tag {
  animation: fadeIn 0.3s ease;
}

.image-preview {
  animation: fadeIn 0.5s ease;
}

/* Permalink styles */
.permalink-container {
  width: 100%;
}

.permalink-helper {
  font-size: 0.85em;
  color: #666;
  margin-top: 5px;
  padding: 5px;
  background-color: rgba(0, 61, 106, 0.03);
  border-radius: 4px;
}

.permalink-input {
  width: 100%;
}

/* Standards main accordion styling */
.standards-main-accordion {
  margin-bottom: 8px;
}

.standards-main-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary-blue);
  font-weight: 600;
  font-size: 1.05em;
}

.standards-icon {
  color: var(--primary-blue);
  font-size: 1.2em;
}

.standards-accordion-content {
  padding: 0;
  margin: 0;
}

/* Nested standards categories styling */
.standards-category {
  border: none;
  border-radius: 0;
  margin: 0;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.standards-category:last-child {
  border-bottom: none;
}

.standards-category .right-sidebar-title {
  color: var(--primary-blue);
  margin-left: 15px;
}
</style>
